import {
  useGetBusinessPartnerById,
  useGetShippingTypesByBusinessPartnerId,
} from '@/api/business-partner/business-partner.hooks.ts';
import { useCart, useUpdateCartShippingType } from '@/api/carts/carts.hooks.ts';
import { CircularLoaderContainer } from '@/components/base/circular-loader-container.component';
import { DEFAULT_SHIPPING_TYPE_ID } from '@/constants/default-shipping-type-id';
import CardContent from '@components/base/card/card-content.component.tsx';
import CardHeader from '@components/base/card/card-header.component.tsx';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { shippingTypeSchema } from '@validation/shipping-type.schema.ts';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

export function CartShippingTypeCard() {
  const { t } = useTranslation('cart');
  const { data: cart, isPending: cartIsPending } = useCart();
  const { data: businessPartner, isPending: businessPartnerIsPending } =
    useGetBusinessPartnerById();
  const { data: shippingTypes, isPending: shippingTypesIsPending } =
    useGetShippingTypesByBusinessPartnerId();
  const {
    mutate: updateCartShippingType,
    isPending: updateCartShippingTypeIsPending,
  } = useUpdateCartShippingType();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    trigger,
  } = useForm({
    mode: 'onChange',
    resolver: zodResolver(shippingTypeSchema),
    values: {
      shippingTypeId:
        cart?.shippingType?.id ??
        businessPartner?.defaultShippingTypeId ??
        DEFAULT_SHIPPING_TYPE_ID,
    },
  });

  const onSubmit = async (data: z.infer<typeof shippingTypeSchema>) => {
    console.log('on submit shipping type', data.shippingTypeId);
    updateCartShippingType({
      shippingTypeId: data.shippingTypeId.toString(),
    });
  };

  useEffect(() => {
    if (
      cartIsPending ||
      businessPartnerIsPending ||
      updateCartShippingTypeIsPending
    ) {
      return;
    }
    if (cart?.shippingType?.id === watch('shippingTypeId')) {
      return;
    }
    const validateAndSubmit = async () => {
      const isValid = await trigger('shippingTypeId');
      if (isValid) {
        handleSubmit(onSubmit)();
      }
    };

    if (watch('shippingTypeId')) {
      validateAndSubmit();
    }
  }, [watch('shippingTypeId')]);

  return (
    <Card
      sx={{
        width: '100%',
      }}
    >
      <CardHeader title={t('shippingType.title')} />
      <CardContent
        component="form"
        sx={{ width: '100%' }}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack spacing={4}>
          {shippingTypesIsPending || cartIsPending ? (
            <CircularLoaderContainer />
          ) : shippingTypes ? (
            <>
              <FormControl variant="filled" sx={{ width: '100%' }}>
                <InputLabel id="shipping-type-select-label">
                  {t('shippingType.label')}
                </InputLabel>
                <Controller
                  name="shippingTypeId"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="shipping-type-select-label"
                      error={!!errors.shippingTypeId}
                    >
                      <MenuItem value="default">
                        {t('shippingType.select')}
                      </MenuItem>
                      {shippingTypes.map((shippingType) => (
                        <MenuItem key={shippingType.id} value={shippingType.id}>
                          {shippingType.name} -{' '}
                          {shippingType.shippingCost.toFixed(2)} €
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText error={!!errors.shippingTypeId}>
                  {errors.shippingTypeId?.message}
                </FormHelperText>
              </FormControl>
            </>
          ) : (
            <MenuItem>{t('shippingType.notFound')}</MenuItem>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
